/**
 * AssociationLookupSelector — Issue #344.
 *
 * Shared combobox-style selector for customer / employee / channel partner
 * ID fields. Replaces manual UUID inputs so admins can search by name and
 * the form still submits the underlying ID.
 *
 * Red lines:
 *   - Submitted value is ALWAYS the real backend ID (never the typed string).
 *   - All suggestions come from `useAssociationLookup`, which calls the
 *     typed api-client. No mock data, no in-memory list filtering.
 *   - Display strings (names) are never echoed to logs or analytics from
 *     this component.
 *   - Uses existing UI primitives + lucide-react; no inline SVG.
 *   - Avoids dynamic Tailwind class generation; classes are statically
 *     written so JIT can pick them up at build time.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useAssociationLookup,
  type AssociationLookupItem,
  type AssociationLookupKind,
} from '@/hooks/useAssociationLookup';

export interface SelectedAssociation {
  /** Backend record id that the form should submit. */
  id: string;
  /** Human-readable name used to render the input value when collapsed. */
  displayName: string;
  /** Lookup kind (echoed so consumers can reuse in mixed-kind contexts). */
  kind: AssociationLookupKind;
}

export interface AssociationLookupSelectorProps {
  kind: AssociationLookupKind;
  /** Optional id for the underlying input — useful for `<Label htmlFor>`. */
  id?: string;
  /** Currently selected item (or null/undefined when nothing is chosen). */
  value: SelectedAssociation | null;
  /** Invoked with the selected item, or null when the user clears it. */
  onChange: (next: SelectedAssociation | null) => void;
  /** Placeholder text shown in the input when nothing is typed. */
  placeholder?: string;
  /** Whether the parent form considers this field required. */
  required?: boolean;
  /** Disables interaction. */
  disabled?: boolean;
  /** Extra class names for the outer wrapper. */
  className?: string;
  /** Optional suggestion cap (1-20). */
  limit?: number;
  /** Optional empty-state hint. */
  emptyHint?: string;
}

const KIND_LABEL: Record<AssociationLookupKind, string> = {
  customer: '客户',
  user: '账号',
  employee: '员工',
  channel_partner: '渠道商',
};

export function AssociationLookupSelector(
  props: AssociationLookupSelectorProps,
): ReactNode {
  const {
    kind,
    id: idProp,
    value,
    onChange,
    placeholder,
    required,
    disabled,
    className,
    limit,
    emptyHint,
  } = props;
  const reactId = useId();
  const inputId = idProp ?? `assoc-${kind}-${reactId}`;
  const listboxId = `${inputId}-listbox`;

  // `displayQuery` is what the user sees in the input. It is set to the
  // selected name when a value is committed, and to the keystroke buffer
  // while the dropdown is open. The form-level value is never overwritten
  // from this string.
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const { items, loading, error } = useAssociationLookup({
    kind,
    query,
    enabled: open && !disabled,
    limit,
  });

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close the dropdown when clicking outside the wrapper.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return (): void => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [items.length]);

  // What the input displays. When the dropdown is open we show the typed
  // text; when closed we show the selected name (or empty).
  const displayValue = open ? query : value?.displayName ?? '';

  const handleSelect = (item: AssociationLookupItem): void => {
    onChange({
      id: item.id,
      displayName: item.displayName,
      kind: item.kind as AssociationLookupKind,
    });
    setQuery('');
    setOpen(false);
  };

  const handleClear = (): void => {
    onChange(null);
    setQuery('');
    setOpen(false);
  };

  const handleFocus = (): void => {
    if (disabled) return;
    setQuery('');
    setOpen(true);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
    if (!open) setOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (items.length === 0 ? 0 : (prev + 1) % items.length));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) =>
        items.length === 0 ? 0 : (prev - 1 + items.length) % items.length,
      );
    } else if (event.key === 'Enter') {
      if (!open) return;
      event.preventDefault();
      const candidate = items[activeIndex];
      if (candidate) handleSelect(candidate);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && !disabled;
  const showEmpty =
    showDropdown && !loading && error === null && items.length === 0;

  const placeholderText = useMemo(
    () => placeholder ?? `搜索${KIND_LABEL[kind]}姓名…`,
    [placeholder, kind],
  );

  return (
    <div
      ref={wrapperRef}
      className={cn('relative w-full', className)}
      data-testid={`assoc-lookup-${kind}`}
    >
      <div className="relative">
        <Search
          aria-hidden
          className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && items[activeIndex]
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
          required={required && !value}
          disabled={disabled}
          value={displayValue}
          placeholder={placeholderText}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-transparent pl-8 pr-9 py-1 text-sm shadow-sm transition-colors',
            'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
        {value && !disabled ? (
          <button
            type="button"
            aria-label={`清除已选${KIND_LABEL[kind]}`}
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <X aria-hidden className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {value ? (
        // Keep the resolved ID visible (and copyable) for support/debug,
        // but in a muted style so it does not dominate the form.
        <p className="mt-1 text-xs text-muted-foreground" data-testid="assoc-selected-id">
          已选 ID：<span className="font-mono">{value.id}</span>
        </p>
      ) : null}

      {showDropdown ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md"
        >
          {loading ? (
            <div
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
              role="status"
              aria-live="polite"
            >
              <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
              加载中…
            </div>
          ) : null}

          {!loading && error ? (
            <div className="px-3 py-2 text-sm text-destructive" role="alert">
              {error}
            </div>
          ) : null}

          {showEmpty ? (
            <div className="px-3 py-2 text-sm text-muted-foreground" role="status">
              {emptyHint ?? '没有匹配的候选，换个关键字试试'}
            </div>
          ) : null}

          {!loading && !error && items.length > 0
            ? items.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  id={`${listboxId}-option-${index}`}
                  aria-selected={activeIndex === index}
                  data-testid={`assoc-lookup-option-${item.id}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => handleSelect(item)}
                  className={cn(
                    'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground',
                    activeIndex === index
                      ? 'bg-accent text-accent-foreground'
                      : null,
                  )}
                >
                  <span className="font-medium">{item.displayName}</span>
                  {item.subtitle ? (
                    <span className="text-xs text-muted-foreground">
                      {item.subtitle}
                    </span>
                  ) : null}
                </button>
              ))
            : null}
        </div>
      ) : null}
    </div>
  );
}
